/**
 * useKPISubscription Hook
 *
 * Subscribes to real-time KPI updates via WebSocket for a specific site.
 * Provides two-layer update strategy:
 *   1. Optimistic cache patch — instantly updates ProjectDetails.kpis via setQueryData
 *   2. Deferred chart invalidation — debounced (5s) invalidation of all chart query keys
 *
 * Buffers incoming kpi:updated events using requestAnimationFrame to collapse
 * rapid-fire updates (20-50 per telemetry push) into a single React render cycle.
 *
 * Handles reconnection by re-subscribing to the site room and forcing an
 * immediate full invalidation to catch up on missed data.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket } from '../services/socket';
import type { ProjectDetails, KPIData } from '../types/project.types';

interface KPIUpdateEvent {
  site_id: string;
  asset_type: string;
  kpi_name: string;
  kpi_value: number;
  kpi_unit: string;
  timestamp: string;
}

const CHART_QUERY_PREFIXES = [
  'battery-soc',
  'solar-generation',
  'ev-sessions',
  'inline-ts',
  'dynamic-chart',
];

export function useKPISubscription(siteId: string, enabled = true): void {
  const queryClient = useQueryClient();

  const kpiBufferRef = useRef<Map<string, KPIUpdateEvent>>(new Map());
  const flushScheduledRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafIdRef = useRef<number | null>(null);

  /**
   * Optimistically patch the ProjectDetails cache with buffered KPI updates.
   * Updates existing KPI entries in-place or appends new ones.
   */
  const patchProjectDetails = useCallback(
    (updates: KPIUpdateEvent[]) => {
      queryClient.setQueryData<ProjectDetails>(
        ['project', siteId],
        (old) => {
          if (!old || !Array.isArray(old.kpis)) return old;

          const kpis = [...old.kpis];
          for (const event of updates) {
            const idx = kpis.findIndex(
              (k) =>
                k.asset_type === event.asset_type &&
                k.kpi_name === event.kpi_name
            );
            const newKpi: KPIData = {
              asset_type: event.asset_type as KPIData['asset_type'],
              kpi_name: event.kpi_name,
              kpi_value: event.kpi_value,
              kpi_unit: event.kpi_unit,
              recorded_at: event.timestamp,
            };
            if (idx >= 0) {
              kpis[idx] = newKpi;
            } else {
              kpis.push(newKpi);
            }
          }
          return { ...old, kpis };
        }
      );
    },
    [queryClient, siteId]
  );

  /**
   * Invalidate all chart queries for the current site using a predicate
   * that matches any of the known chart query key prefixes.
   */
  const invalidateChartQueries = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key.length >= 2 &&
          typeof key[0] === 'string' &&
          CHART_QUERY_PREFIXES.includes(key[0]) &&
          key[1] === siteId
        );
      },
    });
  }, [queryClient, siteId]);

  /**
   * Schedule a deferred invalidation of chart queries.
   * Resets the 5-second debounce timer on each call so rapid bursts
   * collapse into a single invalidation.
   */
  const scheduleDeferredInvalidation = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      invalidateChartQueries();
      debounceTimerRef.current = null;
    }, 5000);
  }, [invalidateChartQueries]);

  /**
   * Flush the KPI buffer: apply optimistic patch and schedule deferred invalidation.
   * Called from requestAnimationFrame to batch all events received in the same frame.
   */
  const flushBuffer = useCallback(() => {
    const updates = Array.from(kpiBufferRef.current.values());
    kpiBufferRef.current.clear();
    flushScheduledRef.current = false;
    rafIdRef.current = null;

    if (updates.length === 0) return;

    patchProjectDetails(updates);
    scheduleDeferredInvalidation();
  }, [patchProjectDetails, scheduleDeferredInvalidation]);

  useEffect(() => {
    if (!enabled || !siteId) return;

    const socket = getSocket();
    if (!socket) {
      console.warn('Socket not connected, skipping KPI subscription');
      return;
    }

    // Subscribe to site room
    socket.emit('subscribe:site', siteId);

    // Handle incoming KPI updates — buffer and flush on next animation frame
    const handleKPIUpdate = (event: KPIUpdateEvent) => {
      if (event.site_id !== siteId) return;

      kpiBufferRef.current.set(
        `${event.asset_type}:${event.kpi_name}`,
        event
      );

      if (!flushScheduledRef.current) {
        flushScheduledRef.current = true;
        rafIdRef.current = requestAnimationFrame(flushBuffer);
      }
    };

    // Handle reconnection — re-subscribe and force immediate full refresh
    const handleReconnect = () => {
      socket.emit('subscribe:site', siteId);

      // Immediate invalidation (skip debounce) to catch up on missed data
      invalidateChartQueries();
      queryClient.invalidateQueries({ queryKey: ['project', siteId] });
    };

    socket.on('kpi:updated', handleKPIUpdate);
    socket.on('connect', handleReconnect);

    // Cleanup
    return () => {
      socket.off('kpi:updated', handleKPIUpdate);
      socket.off('connect', handleReconnect);
      socket.emit('unsubscribe:site', siteId);

      // Clear pending timers
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }

      // Clear buffer
      kpiBufferRef.current.clear();
      flushScheduledRef.current = false;
    };
  }, [
    siteId,
    enabled,
    queryClient,
    flushBuffer,
    invalidateChartQueries,
  ]);
}
